"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useCreditTopup = void 0;
const react_1 = require("react");
const assistant_credits_1 = require("@app/request/assistant-credits");
const util_1 = require("@app/util");
// Long enough that typing "1500" asks once instead of four times, short enough that the figure
// under the field still feels attached to it.
const QUOTE_DEBOUNCE_MS = 400;
/**
 * The buy side of the credit ledger: what an amount costs, and the way to Stripe.
 *
 * The quote is the server's, re-asked as the amount changes, because the rate and the volume
 * bonus live in exactly one place — the same function the webhook uses on the amount actually
 * paid, so the preview cannot promise credits the purchase does not deliver.
 */
const useCreditTopup = (workspaceId, amount) => {
    const [quote, setQuote] = (0, react_1.useState)(undefined);
    const [isRedirecting, setIsRedirecting] = (0, react_1.useState)(false);
    // The debounce protects against typing, and the opening amount was not typed. Waiting on it
    // would hold back the price list the buy screen builds its choices from, so the screen would
    // render empty for as long as the delay lasts.
    const hasQuoted = (0, react_1.useRef)(false);
    (0, react_1.useEffect)(() => {
        if (!(0, util_1.isValue)(workspaceId) || !(0, util_1.isValue)(amount) || amount <= 0) {
            setQuote(undefined);
            return;
        }
        let isCurrent = true;
        const timeout = setTimeout(() => {
            hasQuoted.current = true;
            void (0, assistant_credits_1.requestAssistantCreditQuote)({ workspace: workspaceId, amount })
                // Below the price list's minimum the server throws rather than answering, and that is a
                // normal thing for a half-typed amount to be — the form states the minimum itself, so
                // there is nothing to report here beyond having no figure to show.
                .catch(() => undefined)
                .then((result) => {
                if (isCurrent)
                    setQuote(result);
            });
        }, hasQuoted.current ? QUOTE_DEBOUNCE_MS : 0);
        return () => {
            isCurrent = false;
            clearTimeout(timeout);
        };
    }, [workspaceId, amount]);
    const checkout = (0, react_1.useCallback)(async (checkoutAmount) => {
        if (!(0, util_1.isValue)(workspaceId))
            return;
        setIsRedirecting(true);
        const session = await (0, assistant_credits_1.requestAssistantCreditCheckout)({
            workspace: workspaceId,
            amount: checkoutAmount,
            // Back to whatever the manager was looking at. The credits do not arrive on this trip —
            // Stripe's webhook grants them — so there is nothing to read on return.
            returnUrl: window.location.href,
        }).catch(() => undefined);
        if (!(0, util_1.isValue)(session)) {
            // `processErrors` in the request already told them what went wrong. Give the button back.
            setIsRedirecting(false);
            return;
        }
        window.location.assign(session.url);
    }, [workspaceId]);
    return { quote, isRedirecting, checkout };
};
exports.useCreditTopup = useCreditTopup;
exports.default = exports.useCreditTopup;
